import React, { useContext, useEffect, useState } from 'react'

import { getCookie } from 'cookies-next'
import {
  CollectionSellingMethodType,
  Visibility,
  FetchElementAuthorizations,
  FetchElementAuthorizationsVariables,
} from 'globalTypes'

import { useQuery } from '@apollo/client'
import { faBriefcase } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import CollectionShareWrapper from 'Root/App/Card/Collection/ShareWrapper'
import ContentShareWrapper from 'Root/App/Card/Content/ShareWrapper'
import FolderShareWrapper from 'Root/App/Card/Folder/ShareWrapper'
import { Context as AuthorizationsContext } from 'Root/App/Contexts/Authorizations'
import Title from 'Root/App/Title'
import Button from 'Root/Component/Button'
import { getElementType } from 'lib/get-element-type'
import { getPricingPageURL } from 'lib/get-pricing-page-url'
import getQueryString from 'lib/get-query-string'
import { Workspace } from 'lib/use-workspace-from-element'
import { useRouter } from 'next/router'

import Loading from './Loading'
import CopyCheckoutLink from './copy-checkout-link'
import QUERY from './fetch-element-authorizations.graphql'
import GenerateLink from './generate-link'
import ShareSectionHeading from './share-section-heading'
import ShareTabs, { ShareTabPanel } from './share-tabs'
import ShareToGroup from './share-to-group'
import ShareToUser from './share-to-user'
import UpdateSharePanelVisibility from './update-visibility'

interface Props {
  id: string
}

const BuyToShareGroups: React.FC = () => {
  const { query } = useRouter()

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <Title size="XS">
        <Trans>
          Passa al piano Business per utilizzare questa funzionalità
        </Trans>
      </Title>
      <Button
        href={getPricingPageURL(getQueryString(query.locale))}
        target="_blank"
        rel="noreferrer"
        iconLeft={<FontAwesomeIcon icon={faBriefcase} />}
      >
        <Trans>Passa a Taylora Business</Trans>
      </Button>
    </div>
  )
}

type ElementFromQuery = NonNullable<FetchElementAuthorizations['element']>

interface ShareWrapperSwitchProps {
  children: React.ReactNode
  element: ElementFromQuery
}

const ShareWrapperSwitch: React.FC<ShareWrapperSwitchProps> = ({
  children,
  element,
}) => {
  const type = getElementType(element.__typename ?? '')

  if (type === 'collection') {
    return (
      <CollectionShareWrapper collection={element as any}>
        {children}
      </CollectionShareWrapper>
    )
  }

  if (type === 'folder') {
    return (
      <FolderShareWrapper folder={element as any}>
        {children}
      </FolderShareWrapper>
    )
  }

  return (
    <ContentShareWrapper content={element as any}>
      {children}
    </ContentShareWrapper>
  )
}

interface ShareBoxProps {
  id: string
  closePanel: () => void
  subscriptionPlanCode?: string | null
  workspaceRole?: string | null
  userElementWorkspace?: Workspace | null
}

const ShareBox: React.FC<ShareBoxProps> = ({
  id,
  closePanel,
  subscriptionPlanCode,
  workspaceRole,
  userElementWorkspace,
}) => {
  const { _ } = useLingui()
  const { data, loading } = useQuery<
    FetchElementAuthorizations,
    FetchElementAuthorizationsVariables
  >(QUERY, {
    variables: { id },
    fetchPolicy: 'cache-first',
  })

  if (loading) {
    return <Loading />
  }

  if (!data || !data.element) {
    return <Loading />
  }

  const element = data.element as ElementFromQuery

  const elementType = getElementType(element.__typename ?? '')

  // Guard properties that may only exist on certain element types (collection)
  const selling = (element as any)?.selling
  const sellingPricing = (element as any)?.sellingPricing
  const sellingMethod = (element as any)?.sellingMethod
  const needRegistration = (element as any)?.need_registration

  const showCheckoutLink =
    elementType === 'collection' &&
    element.visibility === Visibility.public &&
    Boolean(selling) &&
    typeof sellingPricing === 'number' &&
    sellingPricing > 0 &&
    sellingMethod === CollectionSellingMethodType.online &&
    !needRegistration

  const handleOnConfirm = (visibility: Visibility) => {
    if (visibility === Visibility.mine) {
      closePanel()
    }
  }

  return (
    <>
      {elementType !== 'folder' && (
        <UpdateSharePanelVisibility
          id={id}
          element={element as any}
          type={elementType}
          onConfirm={handleOnConfirm}
        />
      )}

      <ShareWrapperSwitch element={element}>
        {showCheckoutLink && (
          <span id="share-by-checkout" className="flex flex-col gap-4">
            <ShareSectionHeading
              title={<Trans>Link al checkout</Trans>}
              subtitle={
                <Trans>
                  Copia il link al checkout del tuo corso per integrarlo nel tuo
                  sito.
                </Trans>
              }
            />
            <CopyCheckoutLink slug={element.slug} />
          </span>
        )}

        <span id="share-by-token" className="flex flex-col gap-4">
          <ShareSectionHeading
            title={
              element.visibility === Visibility.private ||
              element.visibility === Visibility.protected ? (
                <Trans>Condividi tramite link privato</Trans>
              ) : (
                <Trans>Condividi tramite link</Trans>
              )
            }
            subtitle={
              element.visibility === Visibility.private ||
              element.visibility === Visibility.protected ? (
                <Trans>Genera un link privato valido una sola volta.</Trans>
              ) : (
                <Trans>Copia il link per condividerlo con chi vuoi.</Trans>
              )
            }
          />
          <GenerateLink visibility={element.visibility} />
        </span>
      </ShareWrapperSwitch>

      <ShareTabs>
        <ShareTabPanel label={_(msg`Utenti`)}>
          <ShareToUser id={element.id} />
        </ShareTabPanel>

        <ShareTabPanel label={_(msg`Gruppi`)}>
          {subscriptionPlanCode === 'business' ||
          subscriptionPlanCode === 'demo' ||
          workspaceRole === 'manager' ||
          workspaceRole === 'creator' ? (
            <ShareToGroup elementId={element.id} />
          ) : (
            <BuyToShareGroups />
          )}
        </ShareTabPanel>
      </ShareTabs>
    </>
  )
}

const SlideContainer: React.FC<Props> = ({ id }) => {
  const {
    setElementId,
    subscriptionPlanCode,
    userSubscriptionLoading,
    workspace,
    workspaceLoading,
  } = useContext(AuthorizationsContext)

  const [workspaceRole, setWorkspaceRole] = useState<string | undefined>(
    undefined
  )

  useEffect(() => {
    const role = getCookie('workspace_role')
    setWorkspaceRole(typeof role === 'string' ? role : undefined)
  }, [])

  if (userSubscriptionLoading || workspaceLoading) return null

  const shareUserElement = Boolean(
    subscriptionPlanCode && subscriptionPlanCode !== 'essential'
  )

  return (
    <div id="slide-container" className="fixed w-full h-full overflow-auto">
      <div className="flex flex-col h-full mx-auto px-4 py-3 max-w-3xl sm:px-6 sm:py-6 gap-6">
        <Title size="M">
          <Trans>Condividi</Trans>
        </Title>

        {(shareUserElement || workspaceRole === 'manager') && (
          <ShareBox
            id={id}
            closePanel={() => setElementId(undefined)}
            subscriptionPlanCode={subscriptionPlanCode}
            workspaceRole={workspaceRole}
            userElementWorkspace={workspace}
          />
        )}
      </div>
    </div>
  )
}

export default SlideContainer
